import { useState } from 'react';
import Banner from '@savvy-kit/patterns/banner';
import { DISMISS_ANNOUNCEMENT } from 'src/constants/graphql/mutations/dismiss-announcement';
import { graphql } from 'src/gql';

import { useMutationWithErrorLogging } from '@savvy/graphql-hooks/useMutationWithErrorLogging';
import { useQueryWithErrorLogging } from '@savvy/graphql-hooks/useQueryWithErrorLogging';

const DASHBOARD_ANNOUNCEMENTS_QUERY = graphql(/* GraphQL */ `
  query DashboardAnnouncements($id: ID) {
    client(id: $id) {
      enableTaxTab
    }
    currentUser {
      activeAnnouncements {
        id
        announcementName
      }
    }
  }
`);

type DashboardAnnouncementsProps = {
  readonly userId: string;
};

export function DashboardAnnouncements({ userId }: DashboardAnnouncementsProps) {
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());

  const { data } = useQueryWithErrorLogging(DASHBOARD_ANNOUNCEMENTS_QUERY, {
    variables: { id: userId },
  });

  const [dismissAnnouncement] = useMutationWithErrorLogging(DISMISS_ANNOUNCEMENT, {
    refetchQueries: [{ query: DASHBOARD_ANNOUNCEMENTS_QUERY, variables: { id: userId } }],
  });

  const taxTabEnabled = data?.client.enableTaxTab ?? false;
  const activeAnnouncements = data?.currentUser.activeAnnouncements ?? [];

  const handleDismiss = (announcementId: string) => {
    setDismissedIds((prev) => new Set(prev).add(announcementId));
    void dismissAnnouncement({
      variables: { input: { announcementConfigId: announcementId } },
    });
  };

  const visibleAnnouncements = activeAnnouncements.filter((a) => {
    if (a.announcementName === 'tax_now_available' && !taxTabEnabled) return false;
    return !dismissedIds.has(a.id);
  });

  if (visibleAnnouncements.length === 0) return null;

  return (
    <>
      {visibleAnnouncements.map((announcement) =>
        renderAnnouncementBanner(announcement.id, announcement.announcementName, () => handleDismiss(announcement.id)),
      )}
    </>
  );
}

/** Banners! **/

function renderAnnouncementBanner(key: string, announcementName: string, onDismiss: () => void) {
  switch (announcementName) {
    case 'tax_now_available':
      return (
        <Banner
          key={key}
          variant="info"
          className="mb-[28px]"
          title="Your advisor can now help with tax filing! 🇺🇸"
          description="They'll have access to your tax history for more complete financial guidance. Meet with them for more information."
          handleClickX={onDismiss}
        />
      );
    default:
      return null;
  }
}
